import React, { useCallback, useState } from 'react';
import InputField from '../../shared/Fields/InputField';
import PickList, { PICKLIST_TYPES } from '../../shared/Fields/PickList';
import { range } from 'lodash';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../shared/Fields/Checkbox';
import { TrashIcon } from '@heroicons/react/outline';
import { Button } from '../../Reusable/Shared';
import RadioButton from '../../shared/Fields/RadioButton';
import { getStep } from '../../../util/surveyUtils';

const ScoringSchemeForm = ({ questions, onSave, onClose }: any) => {
  const [optionCount, setOptionCount] = useState(1);
  const [scoreValues, setScoreValues] = useState<any>({});
  const [applyTo, setApplyTo] = useState('none'); // 'all', 'select', or 'none'
  const [selectedQuestions, setSelectedQuestions] = useState<any>([]);

  const { t } = useTranslation();
  console.log(scoreValues);

  const removeOption = (index: number) => {
    const newScoreValues: any = { ...scoreValues };
    delete newScoreValues[`option${index + 1}`];

    for (let i = index + 1; i < optionCount; i++) {
      newScoreValues[`option${i}`] = newScoreValues[`option${i + 1}`];
      delete newScoreValues[`option${i + 1}`];
    }

    setScoreValues(newScoreValues);
    setOptionCount(optionCount - 1);
  };

  const applyScoring = () => {
    const less: string[] = [];
    const more: string[] = [];
    const selectedQuestionsSet = new Set(selectedQuestions.map((q: any) => q.id));

    const updatedQuestions = questions.map((question: any, index: number) => {
      if (question.answerOption.length > 0) {
        if (applyTo === 'all' || (applyTo === 'select' && selectedQuestionsSet.has(question.id))) {
          const updatedAnswerOptions = question.answerOption.map((option: any, index: number) => ({
            ...option,
            itemWeight: scoreValues[`option${index + 1}`] || 0
          }));

          const scoreValuesCount = Object.keys(scoreValues).length;
          if (question.answerOption.length < scoreValuesCount) {
            less.push(`"${index}: ${question?.text || ''}"`);
          } else if (question.answerOption.length > scoreValuesCount) {
            more.push(`"${index}: ${question?.text || ''}"`);
          }

          return {
            ...question,
            answerOption: updatedAnswerOptions
          };
        }
      } else {
        less.push(`"${index}: ${question?.text || ''}"`);
      }
      return question;
    });

    return { updatedQuestions, less, more };
  };

  return (
    <div className="p-4">
      <div className="space-y-4">
        {range(optionCount).map((index) => (
          <div key={index} className="flex items-center space-x-4">
            <span className="w-24 flex-shrink-0">
              {t('T_OPTION')} {index + 1}
            </span>
            <div className="w-24 flex-shrink-0">
              <InputField
                type="number"
                id={`option-score-${index}`}
                label={''}
                placeholder={t('T_SCORE')}
                formData={scoreValues}
                formError={{}}
                setFormData={(value: any) => {
                  if (Number(value[`option${index + 1}`]) >= 0)
                    setScoreValues({
                      ...scoreValues,
                      [`option${index + 1}`]: value[`option${index + 1}`]
                    });
                }}
                fieldKey={`option${index + 1}`}
                required={false}
                step={getStep(scoreValues[`option${index + 1}`])}
                min={0}
                className="w-full"
              />
            </div>
            <span className="flex-shrink-0">{t('T_POINTS')}</span>
            {optionCount > 1 && (
              <button
                className="text-gray-500 transition-colors duration-200 hover:text-red-500"
                onClick={() => removeOption(index)}
                aria-label="Remove option"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        ))}
        <button
          className="text-blue-500 hover:text-blue-700"
          onClick={() => setOptionCount(optionCount + 1)}
        >
          + {t('T_ADD_ANOTHER')}
        </button>
      </div>

      <div className="mt-6 space-y-2">
        <div className="flex w-full flex-row  space-x-10">
          <RadioButton
            selectedField={applyTo === 'all' ? t('T_ADD_SCORING_APPLY_ALL') : 'none'}
            option={t('T_ADD_SCORING_APPLY_ALL')}
            name={t('T_ADD_SCORING_APPLY_ALL')}
            label={t('T_ADD_SCORING_APPLY_ALL')}
            onClick={() => {
              setApplyTo(applyTo === 'all' ? 'none' : 'all');
            }}
          />
          <RadioButton
            selectedField={applyTo === 'select' ? t('T_ADD_SCORING_SELECTED_QUES') : 'none'}
            option={t('T_ADD_SCORING_SELECTED_QUES')}
            name={t('T_ADD_SCORING_SELECTED_QUES')}
            label={t('T_ADD_SCORING_SELECTED_QUES')}
            className="ml-4"
            onClick={() => {
              setApplyTo(applyTo === 'select' ? 'none' : 'select');
            }}
          />
        </div>
      </div>

      {applyTo === 'select' && (
        <div className="mt-4">
          <PickList
            fieldKey={'id'}
            label={t('T_SELECT_QUESTIONS')}
            required={false}
            fieldValues={['text']}
            selectedItems={selectedQuestions}
            onSelectedChange={(items: any) => setSelectedQuestions(items)}
            items={questions}
            disabled={false}
            readOnly={false}
            hasMore={false}
            type={PICKLIST_TYPES.MULTI}
          />
        </div>
      )}
      <div className="mt-6 flex justify-end space-x-4">
        <Button
          color={'secondary'}
          width={'100px'}
          onClick={onClose}
          disabled={false}
          classes="text-ellipsis overflow-hidden"
          id={'secondary-button'}
        >
          {t('T_CANCEL')}
        </Button>

        <Button
          color={'primary'}
          width={'100px'}
          onClick={() => {
            onSave(applyScoring());
          }}
          disabled={applyTo === 'none'}
          classes="text-ellipsis overflow-hidden"
          id={'primary-button'}
        >
          {t('T_SAVE')}
        </Button>
      </div>
    </div>
  );
};

export default ScoringSchemeForm;
